import { isAcTerminalId } from "../ac";
import { hasMultiPlus2x120Input } from "../ac-supply";
import type { DefLookup } from "../catalog";
import { AC_CIRCUIT_PROFILES, AC_PROTECTION_PROFILES } from "../catalog/ac-circuits";
import { rvPanelProtectionProfiles } from "../catalog/rv-distribution";
import { effectiveTerminals } from "../effective";
import { getSimulateStateSpec } from "../simulate/states";
import type { ComponentDefinition, ComponentNode, Issue, System, Terminal } from "../types";
import { matchesAcSafetyIdentity } from "./ac-safety-identity";
import { buildGroundGraph, ISOLATION_TRANSFORMER } from "./grounding";
import { buildGraph, intraLinks, reachable, v, type Edge, type Graph, type Vertex } from "./graph";

type Pole = "l" | "l1" | "l2" | "n" | "pe";
type Component = { node: ComponentNode; def: ComponentDefinition; terms: Terminal[] };
type Endpoint = Component & { terminal: Terminal };
type Pair = { component: Component; line: Vertex; neutral: Vertex; earth?: Vertex; ambiguousEarth: boolean; sourceKey: string };
type Protection = { nodeId: string; from: Vertex; to: Vertex; ratingA: number };
type Walk = { vertices: Set<Vertex>; cables: Set<string> };

const MAX_STATE_COMBINATIONS = 64;
// Exact published devices; a name, terminal rating or an inline DC fuse is
// never evidence of an AC breaker. Generic panels supply explicit planning
// assumptions in AC_PROTECTION_PROFILES and retain the verification notice.
const BREAKERS: Record<string, number> = {
  "breaker-ac-blue-sea-7294": 16,
  "breaker-ac-blue-sea-7560": 30,
  "breaker-ac-blue-sea-3091": 16,
  "shore-elci-blue-sea-8100": 30,
};
const LEAKAGE_DEVICES = new Set(["shore-elci-blue-sea-8100", "breaker-ac-blue-sea-3091", "dist-ac-gfci-leviton-gftr2"]);
const PROTECTED_EQUIPMENT = new Set([
  "dist-ac-outlet-120", "dist-ac-outlet-230", "dist-ac-gfci-leviton-gftr2",
]);

function pole(terminal: Terminal | undefined): Pole | undefined {
  if (!terminal) return undefined;
  if (isAcTerminalId(terminal.id)) {
    const suffix = terminal.id.toLowerCase().split(/[-_]/).at(-1);
    if (["l", "l1", "l2", "n", "pe"].includes(suffix ?? "")) return suffix as Pole;
  }
  return terminal.polarity === "ground" ? "pe" : undefined;
}
const linePole = (value: Pole | undefined): boolean => value === "l" || value === "l1" || value === "l2";
const stem = (terminal: Terminal) => terminal.id.replace(/[-_](?:l[12]?|n|pe)$/i, "");
const label = ({ node, def }: Component) => node.label ?? def.shortName ?? def.name;
const voltage = (def: ComponentDefinition) => def.acVoltage ??
  ((def.voltage ?? 0) >= 100 ? def.voltage : undefined);

function pairs(component: Component, direction: "input" | "output"): Pair[] {
  const earths = component.terms.filter(t => pole(t) === "pe");
  return component.terms.filter(t => linePole(pole(t)) && t.direction === direction).flatMap(line => {
    const neutral = component.terms.find(t => pole(t) === "n" && stem(t) === stem(line));
    if (!neutral) return [];
    const matching = earths.filter(t => stem(t) === stem(line));
    // One unscoped chassis/PE can serve several ports. A PE explicitly
    // named for another AC port cannot supply this port by default.
    const common = earths.length === 1 &&
      (!isAcTerminalId(earths[0].id) || /^ac[-_](pe|earth|ground|gnd|chassis)$/i.test(earths[0].id)) ? earths[0] : undefined;
    const earth = matching.length === 1 ? matching[0] : common;
    return [{ component, line: v(component.node.id, line.id), neutral: v(component.node.id, neutral.id),
      earth: earth && v(component.node.id, earth.id), ambiguousEarth: !earth && earths.length > 0, sourceKey: `${component.node.id}:${stem(line)}` }];
  });
}

/** Physical paths only. Unknown terminal names cannot join wires; an AC/DC
 * converter or isolation transformer never creates a synthetic copper bridge. */
function walk(graph: Graph, endpoints: Map<Vertex, Endpoint>, start: Vertex,
  allow: (end: Endpoint) => boolean, cut: (edge: Edge) => boolean = () => false,
  stop: ReadonlySet<Vertex> = new Set()): Walk {
  const vertices = new Set<Vertex>();
  const cables = new Set<string>();
  if (!endpoints.has(start)) return { vertices, cables };
  const queue = [start];
  vertices.add(start);
  for (let i = 0; i < queue.length; i++) {
    const at = queue[i];
    if (at !== start && stop.has(at)) continue;
    for (const { vertex, via } of graph.adj.get(at) ?? []) {
      const end = endpoints.get(vertex);
      if (!end || !allow(end) || cut(via)) continue;
      if (via.kind === "intra") {
        const current = endpoints.get(at)!;
        if (current.def.id === ISOLATION_TRANSFORMER ||
          (current.def.category !== "distribution" && pole(current.terminal) !== "pe")) continue;
      }
      if (via.kind === "cable") cables.add(via.cableId);
      if (vertices.has(vertex)) continue;
      vertices.add(vertex);
      queue.push(vertex);
    }
  }
  return { vertices, cables };
}

function isNeutralPost(end: Endpoint): boolean {
  // Polarity-agnostic junctions can carry a conductor, but DC rails, signals,
  // converter terminals and PE cannot supply a missing AC neutral.
  return !isAcTerminalId(end.terminal.id) && end.terminal.polarity === "neutral" &&
    end.def.category === "distribution";
}

function protectionLinks(components: Component[]): Protection[] {
  return components.flatMap(({ node, def }) => {
    if (!matchesAcSafetyIdentity(node, def)) return [];
    const rating = BREAKERS[def.id];
    const profiles = rating ? [{ from: "ac-in-l", to: "ac-out-l", ratingA: rating }] :
      AC_PROTECTION_PROFILES[def.id] ?? rvPanelProtectionProfiles(def);
    return profiles.map(profile => ({ nodeId: node.id, ratingA: profile.ratingA,
      from: v(node.id, profile.from), to: v(node.id, profile.to) }));
  });
}
function isProtectionEdge(edge: Edge, protection: Protection): boolean {
  if (edge.kind !== "intra" || edge.link.nodeId !== protection.nodeId) return false;
  const a = v(edge.link.nodeId, edge.link.from), b = v(edge.link.nodeId, edge.link.to);
  return (a === protection.from && b === protection.to) || (a === protection.to && b === protection.from);
}

/** Design checks cover every distinct conducting selector position. Open
 * states are retained for explicit state tests, but empty/subset contact maps
 * need not multiply design combinations: every physical fault they expose is
 * already present in a superset. Never combine mutually exclusive contacts. */
export function acCircuitDesignStates(components: Component[]): { states: Record<string, string>[]; truncated: boolean } {
  let states: Record<string, string>[] = [{}];
  for (const { node, def, terms } of components) {
    if (def.category !== "distribution" || !terms.some(t => isAcTerminalId(t.id))) continue;
    const options = getSimulateStateSpec(def)?.options;
    if (!options?.some(option => option.intraLinks)) continue;
    const candidates = options.map(option => ({ option, links: new Set(
      (option.ampOverride === 0 ? [] : intraLinks(node, def, [], option))
        .map(link => [link.from, link.to].sort().join("|")),
    ) }));
    const maximal = candidates.filter((candidate, i) => !candidates.some((other, j) =>
      i !== j && (other.links.size > candidate.links.size || j < i) &&
      [...candidate.links].every(link => other.links.has(link))));
    if (!maximal.length) continue;
    if (states.length * maximal.length > MAX_STATE_COMBINATIONS) return { states, truncated: true };
    states = states.flatMap(state => maximal.map(({ option }) => ({ ...state, [node.id]: option.id })));
  }
  return { states, truncated: false };
}

/**
 * These are diagram checks, not an AC solver or installation approval.
 * Victron VE Transfer Switch installation §3 requires separate source/load
 * conductors, external overcurrent/leakage protection and common grounding:
 * https://www.victronenergy.com/media/pg/VE_Transfer_Switch/en/installation.html
 * MultiPlus-II installation §4.4 specifies L/N identity, uninterrupted PE,
 * mode-dependent N/PE bonding and output protection:
 * https://www.victronenergy.com/media/pg/MultiPlus-II_230V/en/installation.html
 * Blue Sea's inverter sub-panel example explains interlocking and destructive
 * backfeed when source and inverter-output buses are joined:
 * https://www.bluesea.com/support/articles/80/Wiring_Inverter_Load_Group_%5Bsub_Panels%5D
 *
 * No protective trip, fault impedance, leakage sensitivity or AC load flow is
 * inferred. W/V is only a lower bound on nominal equipment input current;
 * unspecified power factor/inrush cannot make that lower bound sufficient.
 */
export function detectAcCircuits(system: System, lookup: DefLookup,
  componentStates?: Record<string, string>): Issue[] {
  const components: Component[] = [];
  const endpoints = new Map<Vertex, Endpoint>();
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = lookup(node.definitionId, node.customDefinition);
    if (!def) continue;
    const component = { node, def, terms: effectiveTerminals(def, node) };
    components.push(component);
    for (const terminal of component.terms) endpoints.set(v(node.id, terminal.id), { ...component, terminal });
  }
  const acComponents = components.filter(c => c.terms.some(t => isAcTerminalId(t.id)));
  const connected = new Set(system.cables.flatMap(c => [v(c.fromNodeId, c.fromTerminalId), v(c.toNodeId, c.toTerminalId)]));
  const wiredAc = acComponents.filter(c => c.terms.some(t => isAcTerminalId(t.id) && connected.has(v(c.node.id, t.id))));
  if (!wiredAc.length) return [];
  const sourcePairs = acComponents.filter(c => c.def.category !== "distribution" || c.def.id === ISOLATION_TRANSFORMER)
    .flatMap(c => pairs(c, "output"));
  const sourceVertices = new Set(sourcePairs.flatMap(pair => [pair.line, pair.neutral]));
  const inputs = acComponents.flatMap(c => pairs(c, "input"));
  const protections = protectionLinks(components);
  let groundGraph: Graph | undefined;
  const earthPaths = new Map<Vertex, Set<Vertex>>();
  const issues = new Map<string, Issue>();
  const add = (code: string, severity: Issue["severity"], nodeIds: string[], cableIds: Iterable<string>,
    vars?: Record<string, string | number>, identity?: string) => {
    const uniqueNodes = [...new Set(nodeIds)].sort();
    const key = `${code}:${identity ?? uniqueNodes.join("|")}`;
    const previous = issues.get(key);
    if (previous) {
      previous.cableIds = [...new Set([...(previous.cableIds ?? []), ...cableIds])].sort();
      previous.nodeIds = [...new Set([...(previous.nodeIds ?? []), ...uniqueNodes])].sort();
      return;
    }
    issues.set(key, { code, severity, title: { key: `diag.${code}.title`, vars },
      message: { key: `diag.${code}.message`, vars }, fix: { key: `diag.${code}.fix` },
      nodeIds: uniqueNodes, cableIds: [...new Set(cableIds)].sort() });
  };
  const suppliedInputs = new Set<Vertex>();
  const unsuppliedInputs = new Map<Vertex, { pair: Pair; cables: Set<string> }>();
  const combinations = componentStates ? { states: [componentStates], truncated: false } : acCircuitDesignStates(acComponents);
  if (combinations.truncated) add("ac-state-coverage-unverified", "warning", acComponents.map(c => c.node.id), []);

  for (const state of combinations.states) {
    const graph = buildGraph(system, lookup, { simulate: { componentStates: state } });
    const conductorWalk = (start: Vertex, kind: "l" | "n", cut?: (edge: Edge) => boolean, targetSource?: Vertex) =>
      walk(graph, endpoints, start, end => {
        const vertex = v(end.node.id, end.terminal.id);
        if (targetSource && sourceVertices.has(vertex) && vertex !== targetSource) return false;
        return (kind === "l" ? linePole(pole(end.terminal)) : pole(end.terminal) === "n") || isNeutralPost(end);
      }, cut, sourceVertices);
    const hasOpenContact = (reached: Walk, kind: "l" | "n") => kind === "l" && acComponents.some(component => {
      if (component.def.category !== "distribution") return false;
      const selected = state[component.node.id];
      if (!selected) return false;
      return getSimulateStateSpec(component.def)?.options.some(option =>
        intraLinks(component.node, component.def, system.cables, option).some(link => {
          const from = v(component.node.id, link.from), to = v(component.node.id, link.to);
          const fromPole = pole(endpoints.get(from)?.terminal), toPole = pole(endpoints.get(to)?.terminal);
          if (!linePole(fromPole) || !linePole(toPole)) return false;
          // A one-pole branch may intentionally leave N connected while L is
          // off. An inactive alternative source (both poles open) cannot hide
          // a broken conductor in the selected supply.
          const neutralFrom = from.replace(/([-_])l[12]?$/i, "$1n");
          const neutralTo = to.replace(/([-_])l[12]?$/i, "$1n");
          if (!graph.adj.get(neutralFrom)?.some(edge => edge.vertex === neutralTo && edge.via.kind === "intra")) return false;
          return (reached.vertices.has(from) || reached.vertices.has(to)) &&
            !graph.adj.get(from)?.some(edge => edge.vertex === to && edge.via.kind === "intra");
        })) ?? false;
    });
    const leakageDevices = acComponents.filter(c => LEAKAGE_DEVICES.has(c.def.id) && matchesAcSafetyIdentity(c.node, c.def));
    const leakageCut = (edge: Edge) => edge.kind === "intra" && leakageDevices.some(c => c.node.id === edge.link.nodeId) &&
      [edge.link.from, edge.link.to].some(id => /[-_]in[-_][ln]$/i.test(id));
    for (const device of leakageDevices) {
      const lineInput = v(device.node.id, "ac-in-l");
      const cutDevice = (edge: Edge) => edge.kind === "intra" && edge.link.nodeId === device.node.id;
      const sourceAtInput = conductorWalk(lineInput, "l", cutDevice);
      for (const output of pairs(device, "output")) {
        if (!connected.has(output.line) && !connected.has(output.neutral)) continue;
        const outsideLine = conductorWalk(output.line, "l", cutDevice);
        const outsideNeutral = conductorWalk(output.neutral, "n", cutDevice);
        const fedLine = sourcePairs.filter(source => outsideLine.vertices.has(source.line));
        const fedNeutral = sourcePairs.filter(source => outsideNeutral.vertices.has(source.neutral));
        if (!fedLine.length && !fedNeutral.length) continue;
        const reversed = fedLine.length > 0 && !sourcePairs.some(source => sourceAtInput.vertices.has(source.line));
        add(reversed ? "ac-protection-reversed" : "ac-leakage-bypassed", "warning", [device.node.id],
          [...outsideLine.cables, ...outsideNeutral.cables], { label: label(device) }, output.line);
      }
    }
    const seenIslands = new Set<Vertex>();
    // A passive copper island containing unlike declared conductors is direct
    // evidence of a mismatch, including faults hidden behind a neutral post.
    for (const [vertex, endpoint] of endpoints) {
      if (!linePole(pole(endpoint.terminal)) || seenIslands.has(vertex)) continue;
      const island = walk(graph, endpoints, vertex, end => pole(end.terminal) !== undefined || isNeutralPost(end));
      for (const member of island.vertices) seenIslands.add(member);
      if (!island.cables.size) continue;
      const poles = new Set([...island.vertices].map(member => pole(endpoints.get(member)!.terminal)));
      if (poles.has("n") || poles.has("pe") || (poles.has("l1") && poles.has("l2"))) {
        const nodeIds = [...island.vertices].map(member => endpoints.get(member)!.node.id);
        add("ac-conductor-mismatch", "critical", nodeIds, island.cables);
      }
    }
    // Trace N/PE bonds through junctions as well as direct cables. Their
    // acceptability depends on source/mode and is never inferred here.
    const checkedBonds = new Set<Vertex>();
    for (const [vertex, endpoint] of endpoints) {
      if (pole(endpoint.terminal) !== "n" || checkedBonds.has(vertex)) continue;
      const island = walk(graph, endpoints, vertex, end => ["n", "pe"].includes(pole(end.terminal) ?? "") || isNeutralPost(end));
      island.vertices.forEach(member => checkedBonds.add(member));
      if (island.cables.size && [...island.vertices].some(member => pole(endpoints.get(member)?.terminal) === "pe")) {
        add("ac-neutral-earth-link-unverified", "warning",
          [...island.vertices].map(member => endpoints.get(member)!.node.id), island.cables);
      }
    }
    // Parallel sources are unsafe unless their synchronization/control design
    // has been established elsewhere. Copper connectivity cannot establish it.
    const checkedSources = new Set<Vertex>();
    for (const source of sourcePairs) {
      if (checkedSources.has(source.line)) continue;
      const reached = conductorWalk(source.line, "l");
      const others = sourcePairs.filter(other => reached.vertices.has(other.line));
      others.forEach(other => checkedSources.add(other.line));
      if (others.length > 1) add("ac-sources-paralleled", "critical",
        others.map(other => other.component.node.id), reached.cables);
    }

    const checkedNeutrals = new Set<Vertex>();
    for (const source of sourcePairs) {
      if (checkedNeutrals.has(source.neutral)) continue;
      const reached = conductorWalk(source.neutral, "n");
      const owners = new Set(sourcePairs.filter(other => reached.vertices.has(other.neutral))
        .map(other => other.component.node.id));
      sourcePairs.filter(other => reached.vertices.has(other.neutral)).forEach(other => checkedNeutrals.add(other.neutral));
      if (owners.size > 1) add("ac-source-neutral-shared", "warning", [...owners], reached.cables);
    }

    const completeLoads: Array<{ pair: Pair; source: Pair; current: number; line: Walk }> = [];
    for (const pair of inputs) {
      const { component } = pair;
      // The 2x120 V manual's single-phase diagram leaves L2 open. Its shared
      // neutral belongs to the L1 circuit and does not prove a broken L2 lead.
      // A drawn L2 still receives all conductor/source-pair checks below.
      if (pair.line === v(component.node.id, "ac-in-l2") && !connected.has(pair.line) &&
          hasMultiPlus2x120Input(component.node, component.def)) continue;
      if (!connected.has(pair.line) && !connected.has(pair.neutral)) continue;
      const lines = conductorWalk(pair.line, "l"), neutrals = conductorWalk(pair.neutral, "n");
      const lineSources = sourcePairs.filter(source => lines.vertices.has(source.line));
      const neutralSources = sourcePairs.filter(source => neutrals.vertices.has(source.neutral));
      const matched = lineSources.filter(source => neutralSources.some(other => source.sourceKey === other.sourceKey));
      const witness = new Set([...lines.cables, ...neutrals.cables]);
      if (lineSources.length && neutralSources.length && !matched.length) {
        add("ac-source-pair-mismatch", "critical", [component.node.id,
          ...lineSources.map(s => s.component.node.id), ...neutralSources.map(s => s.component.node.id)], witness,
        { label: label(component) }, pair.line);
      } else if (connected.has(pair.line) !== connected.has(pair.neutral) ||
          ((lineSources.length > 0) !== (neutralSources.length > 0) &&
            !(lineSources.length === 0 ? hasOpenContact(lines, "l") : hasOpenContact(neutrals, "n")))) {
        add("ac-circuit-incomplete", "warning", [component.node.id], witness, { label: label(component) }, pair.line);
      }
      // A selector can choose an empty input intentionally. Report a completely
      // floating circuit only after none of its design positions found supply.
      if (!matched.length) {
        if (!lineSources.length && !neutralSources.length) unsuppliedInputs.set(pair.line, { pair, cables: witness });
        continue;
      }
      suppliedInputs.add(pair.line);
      for (const source of matched) {
        const sourceVoltage = voltage(source.component.def), equipmentVoltage = voltage(component.def);
        if (sourceVoltage && equipmentVoltage && Math.abs(sourceVoltage - equipmentVoltage) / equipmentVoltage > 0.1) {
          add("ac-voltage-mismatch", "critical", [component.node.id, source.component.node.id], witness,
            { label: label(component), expected: equipmentVoltage, actual: sourceVoltage }, pair.line);
        }
        const sourceWitness = () => matched.length === 1 ? witness : [
          ...conductorWalk(pair.line, "l", undefined, source.line).cables,
          ...conductorWalk(pair.neutral, "n", undefined, source.neutral).cables,
        ];
        for (const port of [pair, source]) {
          if (port.ambiguousEarth) {
            add("ac-earth-port-ambiguous", "warning", [port.component.node.id], sourceWitness(),
              { label: label(port.component), port: endpoints.get(port.neutral)!.terminal.id,
                earthPort: port.neutral }, port.neutral);
          }
        }
        // A completely unwired PE is already reported by detectMissingGround.
        // This additional check rejects a drawn local ground island that does
        // not reach the actual AC source (even if it reaches DC negative).
        const hasEarthWire = component.terms.some(term => pole(term) === "pe" && connected.has(v(component.node.id, term.id)));
        if (hasEarthWire && pair.earth && source.earth && pair.earth !== source.earth) {
          let path = earthPaths.get(pair.earth);
          if (!path) {
            groundGraph ??= buildGroundGraph(system, graph, components);
            path = reachable(groundGraph, pair.earth);
            earthPaths.set(pair.earth, path);
          }
          if (!path.has(source.earth)) {
            // Several source ports may feed one input. Preserve each port's own
            // L/N evidence; two line legs with the same PE pair share one issue.
            const earthPair = JSON.stringify([pair.earth, source.earth]);
            add("ac-source-earth-missing", "warning", [component.node.id, source.component.node.id], sourceWitness(),
              { label: label(component), loadTerminal: endpoints.get(pair.earth)!.terminal.id,
                source: label(source.component), sourceTerminal: endpoints.get(source.earth)!.terminal.id, earthPair }, earthPair);
          }
        }
      }
      const isConsumer = component.def.category === "load" && !pairs(component, "output").length;
      const checkProtection = isConsumer || PROTECTED_EQUIPMENT.has(component.def.id);
      if (checkProtection) {
        for (const device of leakageDevices) {
          const cut = (edge: Edge) => edge.kind === "intra" && edge.link.nodeId === device.node.id &&
            [edge.link.from, edge.link.to].some(id => /[-_]in[-_][ln]$/i.test(id));
          const lineCut = conductorWalk(pair.line, "l", cut);
          const neutralCut = conductorWalk(pair.neutral, "n", cut);
          if (matched.some(source => lineCut.vertices.has(source.line) !== neutralCut.vertices.has(source.neutral))) {
            add("ac-leakage-bypassed", "warning", [device.node.id, component.node.id], witness,
              { label: label(device) }, `${device.node.id}:${pair.line}`);
          }
        }
        const leakageLine = conductorWalk(pair.line, "l", leakageCut);
        const leakageNeutral = conductorWalk(pair.neutral, "n", leakageCut);
        if (component.def.id !== "dist-ac-gfci-leviton-gftr2" &&
            matched.some(source => leakageLine.vertices.has(source.line) || leakageNeutral.vertices.has(source.neutral))) {
          add("ac-leakage-unverified", "warning", [component.node.id],
            [...leakageLine.cables, ...leakageNeutral.cables], { label: label(component) }, pair.line);
        }
        const noProtection = conductorWalk(pair.line, "l", edge => protections.some(p => isProtectionEdge(edge, p)));
        const unprotectedSources = matched.filter(source => noProtection.vertices.has(source.line));
        if (unprotectedSources.length) {
          add("ac-protection-missing", "warning", [component.node.id], noProtection.cables,
            { label: label(component) }, pair.line);
        } else {
          const term = endpoints.get(pair.line)!.terminal;
          if (Number.isFinite(term.maxAmp) && term.maxAmp > 0) {
            const excessive = conductorWalk(pair.line, "l", edge => protections.some(p => p.ratingA <= term.maxAmp && isProtectionEdge(edge, p)));
            if (matched.some(source => excessive.vertices.has(source.line))) {
              add("ac-protection-exceeds-rating", "warning", [component.node.id], excessive.cables,
                { label: label(component), rating: term.maxAmp }, pair.line);
            }
          }
        }
      }
      if (isConsumer && matched.length === 1 &&
          Number.isFinite(component.def.watts) && (component.def.watts ?? 0) > 0) {
        const nominal = voltage(matched[0].component.def);
        if (nominal) completeLoads.push({ pair, source: matched[0], current: component.def.watts! / nominal, line: lines });
      }
    }
    for (const load of completeLoads) {
      const rating = endpoints.get(load.pair.line)!.terminal.maxAmp;
      if (rating > 0 && load.current > rating + 1e-9) add("ac-load-exceeds-rating", "warning",
        [load.pair.component.node.id], load.line.cables,
        { current: Number(load.current.toFixed(1)), rating }, load.pair.line);
    }
    // Branch breakers do not establish the panel main's rating. That limit
    // is explicit in the planning profile, independently of its lug ampacity.
    for (const component of acComponents) {
      const profile = AC_CIRCUIT_PROFILES[component.def.id];
      const rating = profile?.mainRatingA ?? profile?.ratingA;
      if (!rating || !matchesAcSafetyIdentity(component.node, component.def)) continue;
      const inputLines = pairs(component, "input").map(pair => pair.line);
      const loads = completeLoads.filter(load => inputLines.some(input => load.line.vertices.has(input)) &&
        !conductorWalk(load.pair.line, "l", edge => edge.kind === "intra" && edge.link.nodeId === component.node.id)
          .vertices.has(load.source.line));
      const current = loads.reduce((sum, load) => sum + load.current, 0);
      if (current > rating + 1e-9) add("ac-load-exceeds-rating", "warning",
        [component.node.id, ...loads.map(load => load.pair.component.node.id)], loads.flatMap(load => [...load.line.cables]),
        { current: Number(current.toFixed(1)), rating }, component.node.id);
    }
    for (const protection of protections) {
      const loads = completeLoads.filter(load => load.line.vertices.has(protection.to) &&
        !conductorWalk(load.pair.line, "l", edge => isProtectionEdge(edge, protection)).vertices.has(load.source.line));
      const current = loads.reduce((sum, load) => sum + load.current, 0);
      if (current > protection.ratingA + 1e-9) add("ac-load-exceeds-rating", "warning",
        [protection.nodeId, ...loads.map(load => load.pair.component.node.id)], loads.flatMap(load => [...load.line.cables]),
        { current: Number(current.toFixed(1)), rating: protection.ratingA }, `${protection.nodeId}:${protection.to}`);
    }
  }
  // A custom electrical edit invalidates the original profile's protection
  // and PE assumptions even when its copied catalog ID is unchanged.
  for (const component of acComponents) {
    if ((AC_CIRCUIT_PROFILES[component.def.id] || BREAKERS[component.def.id]) &&
        component.terms.some(t => connected.has(v(component.node.id, t.id))) &&
        !matchesAcSafetyIdentity(component.node, component.def)) {
      add("ac-profile-unverified", "warning", [component.node.id], [], { label: label(component) });
    }
  }
  if (!componentStates && !combinations.truncated) {
    for (const [line, { pair, cables }] of unsuppliedInputs) {
      if (!suppliedInputs.has(line)) add("ac-circuit-incomplete", "warning", [pair.component.node.id], cables,
        { label: label(pair.component) }, line);
    }
  }
  add("ac-design-unverified", "info", wiredAc.map(c => c.node.id), []);
  return [...issues.values()];
}
